import { startAddress } from "@/jspreadsheet/utils";
import {
  convertAddressToCoordinates,
  getCellElementSafely,
  getWorksheetInstance,
} from "@/jspreadsheet/jspreadsheet";
import queries from "@/queries";
import { useSpreadsheetStore } from "@/stores/spreadsheet";
import { AccountingPeriod } from "@/types/enums";
import { useQuery } from "@tanstack/react-query";
import type { WorksheetInstance } from "jspreadsheet-ce";
import { useCallback, useEffect, useMemo } from "react";
import { create } from "zustand";
import type { CellInfo } from "@/apis/table/types";
import {
  buildHighlightId,
  countTaggedCellsByRoleAndTag,
  findCellsTaggedWith,
  findHighlightCells,
  taggedCellCountKey,
} from "./cell-highlight-matching";

export { getHighlightId } from "./cell-highlight-matching";

const OVERLAY_CLASS = "overlay-highlight";

const toAddresses = (cells: CellInfo[]) => {
  const addresses: string[] = [];
  for (const cell of cells) addresses.push(cell.address);
  return addresses;
};

/**
 * 하이라이트는 스프레드시트(DOM) 단일 인스턴스를 대상으로 하므로,
 * 어떤 TreeItems 인스턴스에서 선택하든 동시에 두 개가 켜지지 않도록
 * 선택 상태를 전역 store 로 둔다(SSOT). isSelected 도 이 값으로 파생한다.
 */
interface HighlightSelectionState {
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
}

export const useHighlightSelectionStore = create<HighlightSelectionState>(
  (set) => ({
    selectedId: null,
    setSelectedId: (id) => set({ selectedId: id }),
  }),
);

// 오버레이가 적용된 셀 주소와 워크시트는 DOM 단일 대상이므로 모듈 전역으로 추적한다.
// 이렇게 해야 다른 TreeItems 인스턴스에서 선택해도 이전 오버레이가 정리된다.
const overlayAddresses = new Set<string>();
let highlightedWorksheet: WorksheetInstance | null = null;
// 클릭으로 선택한 태그의 셀 주소. hover 미리보기가 끝나면 이 목록으로 되돌린다.
let selectedAddresses: string[] = [];
// 삭제 확인 모달이 떠 있는 동안에는 hover 가 끝나도 미리보기를 유지한다.
// 모달이 마운트될 때 켜고 언마운트될 때 끄므로 수명이 모달과 같다.
let previewHeld = false;

const removeOverlay = () => {
  if (highlightedWorksheet) {
    overlayAddresses.forEach((address) => {
      const cell = getCellElementSafely(highlightedWorksheet!, address);
      if (cell) cell.classList.remove(OVERLAY_CLASS);
    });
  }
  overlayAddresses.clear();
  highlightedWorksheet = null;
};

const applyOverlay = (worksheet: WorksheetInstance, addresses: string[]) => {
  addresses.forEach((address) => {
    const cell = getCellElementSafely(worksheet, address);
    if (cell) {
      overlayAddresses.add(address);
      cell.classList.add(OVERLAY_CLASS);
    }
  });
  highlightedWorksheet = worksheet;
};

/**
 * 셀 오버레이 하이라이트 전용 훅.
 * (tagKey + period)로 매칭되는 셀에 CSS 클래스를 적용/제거한다.
 * DnD 트리 내부에서 사용되므로 불필요한 리렌더를 피하기 위해 store/모듈 전역으로 동작.
 */
export const useCellHighlight = (
  workbookId: string | null,
  tableId: string | null,
) => {
  const setSelectedId = useHighlightSelectionStore(
    (state) => state.setSelectedId,
  );
  const activeSheet = useSpreadsheetStore((state) => state.activeSheet);

  const queryEnabled = !!workbookId && !!tableId;
  const { data: cells } = useQuery({
    ...queries
      .workbook(workbookId ?? "")
      ._ctx.tables(tableId ?? "")
      ._ctx.cells(),
    enabled: queryEnabled,
  });

  const taggedCellCounts = useMemo(
    () => countTaggedCellsByRoleAndTag(cells?.cells ?? []),
    [cells],
  );

  /** 트리 항목 렌더마다 불리므로 셀 전체를 훑지 않고 미리 센 값을 돌려준다. */
  const getTaggedCellCount = useCallback(
    (roleKey: string, tagKey: string) =>
      taggedCellCounts.get(taggedCellCountKey(roleKey, tagKey)) ?? 0,
    [taggedCellCounts],
  );

  const clearHighlight = useCallback(() => {
    removeOverlay();
    selectedAddresses = [];
    previewHeld = false;
    setSelectedId(null);
  }, [setSelectedId]);

  const highlightByTagKey = useCallback(
    (
      roleKey: string,
      tagKey: string,
      begend: AccountingPeriod,
      periodScoped: boolean,
    ) => {
      const worksheet = getWorksheetInstance();
      if (!cells?.cells || !worksheet) return;

      const selectionId = buildHighlightId(roleKey, tagKey, begend);
      const wasSelected =
        useHighlightSelectionStore.getState().selectedId === selectionId;

      // 이전 하이라이트 제거
      removeOverlay();
      selectedAddresses = [];

      // 같은 태그 클릭 시 토글 off
      if (wasSelected) {
        setSelectedId(null);
        return;
      }

      const matchingCells = findHighlightCells(cells.cells, {
        roleKey,
        tagKey,
        begend,
        periodScoped,
      });

      if (matchingCells.length === 0) {
        setSelectedId(null);
        return;
      }

      selectedAddresses = toAddresses(matchingCells);
      applyOverlay(worksheet, selectedAddresses);
      setSelectedId(selectionId);

      // 첫 번째 매칭 셀로 스크롤
      try {
        const coords = convertAddressToCoordinates(
          startAddress(matchingCells[0].address),
        );
        worksheet.updateSelectionFromCoords(coords.col, coords.row);
      } catch {
        // 좌표 변환 에러 무시
      }
    },
    [cells, setSelectedId],
  );

  /**
   * hover 미리보기: 클릭과 같은 규칙으로 셀을 비추되 선택 상태와 스크롤은 건드리지 않는다.
   */
  const previewByTagKey = useCallback(
    (
      roleKey: string,
      tagKey: string,
      begend: AccountingPeriod,
      periodScoped: boolean,
    ) => {
      if (previewHeld) return;
      const worksheet = getWorksheetInstance();
      if (!worksheet) return;
      const addresses = toAddresses(
        findHighlightCells(cells?.cells ?? [], {
          roleKey,
          tagKey,
          begend,
          periodScoped,
        }),
      );
      // 비출 셀이 없으면 클릭으로 선택해 둔 하이라이트를 그대로 둔다.
      if (addresses.length === 0) return;
      removeOverlay();
      applyOverlay(worksheet, addresses);
    },
    [cells],
  );

  /** hover 가 끝나면 클릭으로 선택해 둔 태그의 하이라이트로 되돌린다. */
  const endPreview = useCallback(() => {
    if (previewHeld) return;
    removeOverlay();
    const worksheet = getWorksheetInstance();
    if (!worksheet || selectedAddresses.length === 0) return;
    applyOverlay(worksheet, selectedAddresses);
  }, []);

  /**
   * 삭제 대상 셀 전부를 비추고, releasePreview 전까지 hover 종료에도 유지한다.
   * 삭제 확인 모달이 마운트될 때 부르고, 언마운트될 때 releasePreview 를 부른다.
   */
  const holdPreview = useCallback(
    (roleKey: string, tagKey: string) => {
      const worksheet = getWorksheetInstance();
      if (!worksheet) return;
      removeOverlay();
      applyOverlay(
        worksheet,
        toAddresses(findCellsTaggedWith(cells?.cells ?? [], roleKey, tagKey)),
      );
      previewHeld = true;
    },
    [cells],
  );

  const releasePreview = useCallback(() => {
    previewHeld = false;
    endPreview();
  }, [endPreview]);

  // 시트가 전환되면 이전 시트에 남아있는 overlay highlight 를 정리한다.
  useEffect(() => {
    if (!highlightedWorksheet || highlightedWorksheet === activeSheet) return;
    clearHighlight();
  }, [activeSheet, clearHighlight]);

  useEffect(() => () => clearHighlight(), [clearHighlight]);

  return {
    highlightByTagKey,
    previewByTagKey,
    endPreview,
    holdPreview,
    releasePreview,
    getTaggedCellCount,
    clearHighlight,
  };
};
